/**
 * Tiny HTML helpers. Pure — no DOM, no I/O. Safe to use in a Worker.
 */

/** Escape characters that have special meaning inside HTML text or attributes. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize a URL into something safe to drop into `href`. Strips
 * `javascript:`, `data:`, `vbscript:`. Allows `http(s):`, `mailto:`,
 * relative paths, and `tel:`.
 */
export function safeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '#';
  // eslint-disable-next-line no-script-url
  if (/^javascript:/i.test(trimmed)) return '#';
  if (/^data:/i.test(trimmed)) return '#';
  if (/^vbscript:/i.test(trimmed)) return '#';
  return trimmed;
}

/** Treat anything that doesn't start with a scheme as https://. */
export function ensureHttps(url: string): string {
  const t = url.trim();
  if (!t) return '#';
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  return `https://${t}`;
}

/**
 * Run an outbound URL through the per-render tokenizer if one was
 * provided. Returns the original URL unchanged when:
 *   - tokenizer is undefined (we're rendering inside the editor preview)
 *   - the URL isn't http(s) (mailto:, tel:, etc. have no scraping value)
 *   - the URL is relative (already on our origin)
 *
 * Blocks that emit outbound `<a href>` should pipe their URLs through
 * this AFTER `ensureHttps` + `safeUrl`. That keeps the original
 * sanitisation behavior identical and only adds tokenization on top.
 */
export function maybeTokenize(
  url: string,
  tokenizeUrl: ((url: string) => string) | undefined,
): string {
  if (!tokenizeUrl) return url;
  // Only http(s) destinations are worth obfuscating. mailto:/tel:/sms:
  // schemes don't go through a redirect anyway.
  if (!/^https?:\/\//i.test(url)) return url;
  return tokenizeUrl(url);
}

/** Convert a hex (without `#`) like `4f46e5` → `rgb(79,70,229)`. */
export function hexToRgb(hex: string): string {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return 'rgb(79,70,229)';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgb(${r},${g},${b})`;
}
